import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import introVideoPoster from "../assets/video/new-user-intro-poster.png";
import introVideo from "../assets/video/new-user-intro.mp4";
import { useCurrentUserId } from "../hooks/useCurrentUserId";
import { trackVideoEvent } from "../lib/tracing";
import { Modal } from "./Modal";

const VIDEO_NAME = "new-user-intro";

// Keyed by user, since the intro can wait across sessions and someone else may sign in on the same browser first.
const signupFlowStorageKey = (userId: string) =>
  `3b-new-user-from-signup-flow:${userId}`;

function cameFromSignupFlow(userId: string): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(signupFlowStorageKey(userId)) !== null;
}

export function markCameFromSignupFlow(userId: string): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(signupFlowStorageKey(userId), "true");
}

const VideoFrame = styled("div")`
  width: min(80vw, 800px);
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 16px;
  background: var(--surface-secondary);

  & video {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

export function NewUserIntroVideo() {
  const userId = useCurrentUserId();
  const [eligible] = useState(() => cameFromSignupFlow(userId));
  const [open, setOpen] = useState(false);
  const hasShown = useRef(false);

  useEffect(() => {
    if (hasShown.current || !eligible) return;
    hasShown.current = true;
    localStorage.removeItem(signupFlowStorageKey(userId));
    setOpen(true);
  }, [eligible, userId]);

  if (!open) return null;

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <VideoFrame>
        <video
          src={introVideo}
          poster={introVideoPoster}
          controls
          playsInline
          preload="metadata"
          onPlay={() => trackVideoEvent(VIDEO_NAME, "play")}
          onPause={() => trackVideoEvent(VIDEO_NAME, "pause")}
          onEnded={() => trackVideoEvent(VIDEO_NAME, "ended")}
        />
      </VideoFrame>
    </Modal>
  );
}
